// ============================================================================
// Central config for the Qualitative Research Success Series landing page.
// EDIT COHORT_INFO and WORKSHOP_DATES below to update the schedule everywhere.
// All components read from this file — no dates or times are hardcoded elsewhere.
// ============================================================================

export const REGISTER_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLScPOOvzrrOVHIgJyqc_BuI8j2CbOfStYgAz2tsoWvwsDdrw2Q/viewform?usp=publish-editor";

export const WHATSAPP_URL = "https://wa.me/60102306534";
export const WHATSAPP_DISPLAY = "+60 10 230 6534";

export const BRAND = "Aurea Digital Solutions";
export const AUREA_WEBSITE = "https://aureadigitalsolutions.com";

// Approved final professional portrait of Prof. Dr. Aini Aman.
export const FACILITATOR_PHOTO =
    "https://customer-assets.emergentagent.com/job_research-mastery-2/artifacts/1mhvo7rq_ChatGPT%20Image%20Jul%206%2C%202026%2C%2011_49_03%20AM.png";
export const FACILITATOR_NAME = "Prof. Dr. Aini Aman";

// ---------------------------------------------------------------------------
// COHORT_INFO — edit this to change the current cohort and monthly wording
// ---------------------------------------------------------------------------
export const COHORT_INFO = {
    cohortLabel: "Cohort 1",
    cohortMonth: "August 2026",
    cohortType: "Monthly Cohort",
    frequency: "Every Saturday",
    time: "10.00 am to 12.30 pm",
    timezone: "Malaysia Time",
    mode: "Live Online",
    registrationStatus: "Registration Open",
    monthlyCohortText:
        "New monthly cohorts are available. Each cohort runs every Saturday from 10.00 am to 12.30 pm, Malaysia Time. Register your interest and we will confirm the next available cohort schedule.",
    futureNote: "Future monthly cohort dates will be updated here.",
};

// ---------------------------------------------------------------------------
// WORKSHOP_DATES — the single source of truth for workshop dates.
// Only edit the "date" values here when preparing a new cohort.
// Time is inherited from COHORT_INFO.time.
// ---------------------------------------------------------------------------
export const WORKSHOP_DATES = [
    {
        workshopNo: "01",
        title: "Qualitative Research for Beginners",
        subtitle: "From Research Idea to Interpretive Proposal",
        date: "Saturday, 1 August 2026",
        time: COHORT_INFO.time,
    },
    {
        workshopNo: "02",
        title: "Writing a Strong Qualitative Research Proposal",
        subtitle: "From Introduction to Methodology",
        date: "Saturday, 8 August 2026",
        time: COHORT_INFO.time,
    },
    {
        workshopNo: "03",
        title: "Preparing for Qualitative Data Collection",
        subtitle: "From Sampling to Interview Protocol",
        date: "Saturday, 15 August 2026",
        time: COHORT_INFO.time,
    },
    {
        workshopNo: "04",
        title: "Qualitative Data Analysis Made Simple",
        subtitle: "From Interview Transcript to Codes, Categories and Themes",
        date: "Saturday, 22 August 2026",
        time: COHORT_INFO.time,
    },
    {
        workshopNo: "05",
        title: "Writing and Publishing Qualitative Research",
        subtitle: "From Findings Chapter to Journal Article",
        date: "Saturday, 29 August 2026",
        time: COHORT_INFO.time,
    },
];

// Workshop content (outcomes, focus areas) is keyed by workshopNo so dates and
// content can be maintained independently.
const WORKSHOP_CONTENT = {
    "01": {
        outcome:
            "By the end of this session, you will understand the basic logic of qualitative research and how to turn a topic into a clearer research problem.",
        focus: [
            "Research topic clarity",
            "Research problem framing",
            "Research objectives",
            "Research questions",
            "Basic proposal structure",
        ],
    },
    "02": {
        outcome:
            "By the end of this session, you will understand how to write a more convincing qualitative proposal.",
        focus: [
            "Background of study",
            "Problem statement",
            "Research gap",
            "Research objectives and questions",
            "Qualitative methodology justification",
            "Alignment between problem, theory and method",
        ],
    },
    "03": {
        outcome:
            "By the end of this session, you will know how to prepare for qualitative data collection with greater confidence.",
        focus: [
            "Participant selection",
            "Sampling strategy",
            "Interview protocol",
            "Interview questions",
            "Ethics and consent",
            "Fieldwork readiness",
        ],
    },
    "04": {
        outcome:
            "By the end of this session, you will understand how to move from raw transcript to codes, categories and themes.",
        focus: [
            "Transcript preparation",
            "Coding process",
            "Category development",
            "Theme development",
            "Interpretation",
            "Avoiding shallow analysis",
        ],
    },
    "05": {
        outcome:
            "By the end of this session, you will understand how to write stronger qualitative findings and prepare your work for publication.",
        focus: [
            "Writing findings clearly",
            "Using participant evidence effectively",
            "Linking findings to literature",
            "Strengthening discussion",
            "Writing contribution",
            "Turning thesis findings into journal article structure",
        ],
    },
};

// Combined workshops list used by section components. Do NOT edit this — edit
// WORKSHOP_DATES and WORKSHOP_CONTENT above instead.
export const WORKSHOPS = WORKSHOP_DATES.map((d) => ({
    n: d.workshopNo,
    title: d.title,
    subtitle: d.subtitle,
    date: d.date,
    time: d.time,
    ...WORKSHOP_CONTENT[d.workshopNo],
}));

// Convenience derived strings (kept for backward compatibility with components).
export const COHORT_LABEL = `${COHORT_INFO.cohortLabel} · ${COHORT_INFO.cohortMonth}`;
export const COHORT_NOTE = `${COHORT_INFO.frequency} · ${COHORT_INFO.time} · ${COHORT_INFO.timezone}`;

// ---------------------------------------------------------------------------
// Static content below — safe to keep untouched between cohorts.
// ---------------------------------------------------------------------------
export const OUTCOMES = [
    "Turn a broad topic into a focused qualitative research problem.",
    "Write clearer research objectives and research questions.",
    "Build a stronger qualitative proposal.",
    "Prepare an interview protocol with better questions.",
    "Understand how to select participants and prepare for fieldwork.",
    "Analyse interview transcripts using codes, categories and themes.",
    "Write findings with evidence, interpretation and academic depth.",
    "Prepare your thesis findings for journal publication.",
];

export const PAIN_POINTS = [
    "I have a topic, but I do not know how to turn it into a research problem.",
    "I am not sure whether my study is really qualitative.",
    "I do not know how to write a convincing proposal.",
    "I am confused about interpretive research.",
    "I do not know who to interview or how many participants I need.",
    "I am worried that my interview questions are too weak.",
    "I have transcripts, but I do not know how to code them.",
    "I can find themes, but I do not know how to write findings.",
    "I want to publish, but I do not know how to turn my thesis into an article.",
];

export const MATERIALS = [
    { label: "Workshop slides", note: "Take-home reference for every session" },
    { label: "Proposal structure guide", note: "Framework to build a strong proposal" },
    { label: "Problem statement worksheet", note: "Turn broad topics into focused problems" },
    { label: "Interview protocol template", note: "Ready-to-adapt structure" },
    { label: "Sample interview questions", note: "A prompt library to adapt for your study" },
    { label: "Sample transcript", note: "Practise coding on real material" },
    { label: "Coding worksheet", note: "Step-by-step coding practice" },
    { label: "Theme development guide", note: "From codes to categories to themes" },
    { label: "Findings writing guide", note: "Write a strong findings chapter" },
    { label: "Publication readiness checklist", note: "From thesis to journal article" },
    { label: "Certificate of attendance", note: "For your CV and portfolio" },
];

export const AUDIENCES = [
    "Final year undergraduate students",
    "Master's students",
    "PhD students",
    "Research assistants",
    "Lecturers",
    "Early-career researchers",
    "Young academics",
    "Supervisors who want a refresher",
    "Professionals conducting applied research",
    "Anyone new to qualitative research",
];

export const DECISION_GUIDE = [
    { condition: "You are new to qualitative research", pick: "Start with Workshop 1" },
    { condition: "You are writing your proposal", pick: "Choose Workshops 1 and 2" },
    { condition: "You are preparing for interviews", pick: "Choose Workshop 3" },
    { condition: "You already have interview data", pick: "Choose Workshops 4 and 5" },
    {
        condition: "You want the full journey from idea to publication",
        pick: "Choose the Full Five-Workshop Series",
    },
];

export const CREDENTIALS = [
    "Professor & Research Trainer",
    "Experienced Postgraduate Supervisor",
    "Interpretive Qualitative Research Expert",
    "Academic Publication Mentor",
    "Research Consultant & Project Leader",
];

export const FACILITATOR_QUOTE = {
    text:
        "Qualitative research becomes easier when you understand the logic, not only the method. This series is designed to guide researchers step by step from confusion to clarity.",
    attribution: "Prof. Dr. Aini Aman",
};
